<template>
  <div>
    <v-card-actions v-if="!readOnly" class="mb-1">
      <v-text-field
        v-model="search"
        label="Suchen"
        outlined
        hide-details
        clearable
        class="mr-2"
      ></v-text-field>
      <v-btn
        text
        @click="$emit('update:selectedPermissions', [])"
        :disabled="selectedPermissions.length === 0"
      >
        Zurücksetzen
      </v-btn>
    </v-card-actions>

    <v-text-field
      v-if="readOnly && showSearch"
      v-model="search"
      label="Suchen"
      outlined
      hide-details
      clearable
      dense
      class="mb-3"
    ></v-text-field>

    <v-treeview
      v-if="!readOnly"
      v-model="internalSelectedPermissions"
      dense
      :items="permissionsData"
      :search="search"
      :filter="filter"
      selected-color="primary"
      selection-type="leaf"
      item-disabled="disabled"
      open-on-click
      selectable
      :open.sync="openNodes"
      transition
      expand-icon="mdi-chevron-down"
    >
      <template v-slot:label="{ item }">
        <v-icon v-if="item.icon" dense :disabled="item.disabled" class="mr-1">
          {{ item.icon }}
        </v-icon>
        {{ item.name }}
      </template>
      <template v-slot:append="{ item }">
        <v-menu
          ref="dependencyMenu"
          offset-y
          bottom
          left
          :nudge-width="100"
          :max-width="420"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="item.dependencies"
              small
              depressed
              v-bind="attrs"
              v-on="on"
              :color="getBtnColor(item.dependencies)"
            >
              <v-icon small left>mdi-shield-alert</v-icon>
              Abhängigkeit
            </v-btn>
          </template>
          <v-card>
            <v-card-title class="font-weight-medium subtitle-1 text-wrap">
              Berechtigung setzt weitere Berechtigungen voraus:
            </v-card-title>
            <v-card-text>
              <li
                v-for="permissionId in item.dependencies"
                :key="permissionId"
                :class="
                  internalSelectedPermissions.includes(permissionId)
                    ? 'green--text'
                    : 'red--text'
                "
              >
                {{ getPermissionTitleFromId(permissionId) }}
              </li>
            </v-card-text>
            <v-card-actions>
              <v-btn depressed class="ml-2">Abbrechen</v-btn>
              <v-spacer></v-spacer>
              <v-btn
                depressed
                color="primary"
                class="mr-2"
                @click="addDependencies(item.dependencies)"
              >
                Berechtigungen erteilen
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </template>
    </v-treeview>

    <!-- Read-only view -->
    <v-treeview
      v-else
      dense
      :items="filteredReadOnlyTree"
      :search="search"
      :filter="filter"
      :open.sync="openNodes"
      open-on-click
      transition
      expand-icon="mdi-chevron-down"
    >
      <template v-slot:label="{ item }">
        <v-icon v-if="item.icon" dense class="mr-1">
          {{ item.icon }}
        </v-icon>
        <span>
          {{ item.name }}
        </span>
        <v-chip
          v-if="
            item.isSelected && showPermissionSource && item.permissionSource
          "
          x-small
          :color="getSourceColor(item.permissionSource)"
          text-color="white"
          class="ml-2"
        >
          {{ getSourceLabel(item.permissionSource) }}
        </v-chip>
      </template>
      <template v-slot:prepend="{ item }">
        <v-icon
          v-if="item.isLeaf"
          :color="item.isSelected ? 'success' : 'grey lighten-1'"
          small
        >
          {{ item.isSelected ? "mdi-check-circle" : "mdi-circle-outline" }}
        </v-icon>
      </template>
    </v-treeview>

    <!-- Permission count summary -->
    <div v-if="readOnly && showSummary" class="mt-3 text-caption grey--text">
      <v-icon small left>mdi-information</v-icon>
      {{ selectedCount }} von {{ totalCount }} Berechtigungen aktiv
    </div>
  </div>
</template>

<script>
import { permissionsTree } from "@/data/permission-tree.js";

export default {
  name: "PermissionTreeView",
  props: {
    selectedPermissions: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    showSummary: {
      type: Boolean,
      default: false,
    },
    showPermissionSource: {
      type: Boolean,
      default: false,
    },
    // For showing where each permission comes from
    groupPermissions: {
      type: Array,
      default: () => [],
    },
    rolePermissions: {
      type: Array,
      default: () => [],
    },
    individualPermissions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      openNodes: ["all_access"],
      search: null,
    };
  },
  computed: {
    permissionsData() {
      return permissionsTree;
    },
    internalSelectedPermissions: {
      get() {
        return this.selectedPermissions;
      },
      set(value) {
        this.$emit("update:selectedPermissions", value);
      },
    },
    filter() {
      return this.caseSensitive
        ? (item, search, textKey) => item[textKey].indexOf(search) > -1
        : undefined;
    },
    // Build a filtered tree showing only selected permissions for read-only view
    filteredReadOnlyTree() {
      return this.buildReadOnlyTree(this.permissionsData);
    },
    selectedCount() {
      return this.selectedPermissions.length;
    },
    totalCount() {
      return this.countAllLeafNodes(this.permissionsData);
    },
  },
  methods: {
    getPermissionTitleFromId(permissionId) {
      const findPermission = (permissions, id) => {
        for (const permission of permissions) {
          if (permission.id === id) {
            return permission;
          }
          if (permission.children) {
            const found = findPermission(permission.children, id);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };
      const permission = findPermission(this.permissionsData, permissionId);
      return permission ? permission.name : permissionId;
    },
    addDependencies(dependencies) {
      const newPermissions = [...this.internalSelectedPermissions];
      dependencies.forEach((dependency) => {
        if (!newPermissions.includes(dependency)) {
          newPermissions.push(dependency);
        }
      });
      this.$emit("update:selectedPermissions", newPermissions);
    },
    getBtnColor(dependencies) {
      const allIncluded = dependencies.every((dep) =>
        this.internalSelectedPermissions.includes(dep),
      );
      return allIncluded ? "success" : "error";
    },
    getSourceColor(source) {
      switch (source) {
        case "group":
          return "blue";
        case "role":
          return "purple";
        case "individual":
          return "orange";
        default:
          return "grey";
      }
    },
    getSourceLabel(source) {
      switch (source) {
        case "group":
          return "Gruppe";
        case "role":
          return "Rolle";
        case "individual":
          return "Individuell";
        default:
          return source;
      }
    },
    getPermissionSource(permissionId) {
      if (this.groupPermissions.includes(permissionId)) {
        return "group";
      }
      if (this.rolePermissions.includes(permissionId)) {
        return "role";
      }
      if (this.individualPermissions.includes(permissionId)) {
        return "individual";
      }
      return null;
    },
    buildReadOnlyTree(items) {
      const result = [];
      for (const item of items) {
        const isLeaf = !item.children || item.children.length === 0;
        const isSelected = this.selectedPermissions.includes(item.id);
        const hasSelectedChildren = item.children
          ? this.hasAnySelectedChild(item.children)
          : false;

        if (isSelected || hasSelectedChildren) {
          const newItem = {
            ...item,
            isLeaf,
            isSelected,
            permissionSource: isSelected
              ? this.getPermissionSource(item.id)
              : null,
          };
          if (item.children) {
            newItem.children = this.buildReadOnlyTree(item.children);
          }
          result.push(newItem);
        }
      }
      return result;
    },
    hasAnySelectedChild(children) {
      for (const child of children) {
        if (this.selectedPermissions.includes(child.id)) {
          return true;
        }
        if (child.children && this.hasAnySelectedChild(child.children)) {
          return true;
        }
      }
      return false;
    },
    countAllLeafNodes(items) {
      let count = 0;
      for (const item of items) {
        if (!item.children || item.children.length === 0) {
          count++;
        } else {
          count += this.countAllLeafNodes(item.children);
        }
      }
      return count;
    },
  },
};
</script>
